// Keeps the header cart badge (and anything else reading the "items"
// localStorage key) in sync within the same tab. Native localStorage
// writes don't fire the browser "storage" event in the tab that made
// the write, so components relying only on that event never see their
// own cart updates - only updates made in other tabs. This patches
// localStorage.setItem/removeItem once, at app startup, so every
// write to "items" (from any page, present or future) broadcasts a
// "cartUpdated" event that listeners can subscribe to.

const CART_KEY = 'items';
const CART_EVENT = 'cartUpdated';

// Captured at module load - before App mounts and before any async
// third-party script (e.g. the Tawk.to chat widget, injected later via
// its own <script> tag) has a chance to run and potentially reassign
// localStorage.setItem itself. Cart-mutating code should prefer
// setCartItems() below, which writes through this reference directly
// instead of through whatever localStorage.setItem currently points to.
const nativeSetItem = Storage.prototype.setItem.bind(localStorage);
const nativeRemoveItem = Storage.prototype.removeItem.bind(localStorage);
const nativeClear = Storage.prototype.clear.bind(localStorage);

export function initCartSync() {
  if (window.__cartSyncPatched) return;
  window.__cartSyncPatched = true;

  localStorage.setItem = (key, value) => {
    nativeSetItem(key, value);
    if (key === CART_KEY) {
      window.dispatchEvent(new Event(CART_EVENT));
    }
  };

  localStorage.removeItem = (key) => {
    nativeRemoveItem(key);
    if (key === CART_KEY) {
      window.dispatchEvent(new Event(CART_EVENT));
    }
  };

  localStorage.clear = () => {
    nativeClear();
    window.dispatchEvent(new Event(CART_EVENT));
  };
}

export function getCartItems() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY)) || [];
  } catch {
    return [];
  }
}

// Preferred way for app code to write the cart. Writes straight through
// the native Storage method captured above and dispatches the change
// event itself, so it keeps working even if localStorage.setItem has
// been reassigned by other code after page load.
export function setCartItems(items) {
  nativeSetItem(CART_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event(CART_EVENT));
}

export function subscribeToCartChanges(callback) {
  window.addEventListener(CART_EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(CART_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}
